import React, { useEffect, useMemo, useState } from 'react';
import './NavBadge.css';

const FONT_WEIGHT = 600;
const FONT_STACK = "'IBM Plex Sans', sans-serif";

const _nudgeCache = new Map();
let _canvas;
let _ctx;

/**
 * Dynamische optische Horizontal-Zentrierung.
 *
 * Reine Font-Metriken (advance / tabular-nums) reichen nicht: bei kleiner
 * Schriftgröße verschiebt das Hinting/Anti-Aliasing die sichtbare Tinte einzelner
 * Ziffern (z.B. „9", „3") merklich nach links, ohne dass die Metrik das zeigt.
 * Wir rastern die konkrete Zahl daher in der echten Größe (inkl. devicePixelRatio)
 * auf ein Canvas, ermitteln die tatsächliche Ink-Mitte (Bounding-Box der gesetzten
 * Pixel) und schieben um die Differenz zur Advance-Mitte. Das zentriert jede Zahl
 * optisch — pro Ziffer, pro Größe, pro Gerät (auf dem hochauflösenden iPhone-Display
 * sogar präziser als am PC).
 */
function inkCenterNudge(text, fontPx) {
  if (typeof document === 'undefined') return 0;
  const dpr = window.devicePixelRatio || 1;
  const key = `${text}@${fontPx}@${dpr}`;
  if (_nudgeCache.has(key)) return _nudgeCache.get(key);

  if (!_canvas) {
    _canvas = document.createElement('canvas');
    _ctx = _canvas.getContext('2d', { willReadFrequently: true });
  }
  const w = Math.ceil(fontPx * (text.length + 4));
  const h = Math.ceil(fontPx * 3);
  _canvas.width = Math.ceil(w * dpr);
  _canvas.height = Math.ceil(h * dpr);
  _ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  _ctx.clearRect(0, 0, w, h);
  _ctx.font = `${FONT_WEIGHT} ${fontPx}px ${FONT_STACK}`;
  _ctx.textBaseline = 'middle';
  _ctx.textAlign = 'left';
  _ctx.fillStyle = '#000';
  const originX = fontPx * 2;
  _ctx.fillText(text, originX, h / 2);
  const advance = _ctx.measureText(text).width;

  const data = _ctx.getImageData(0, 0, _canvas.width, _canvas.height).data;
  let minX = Infinity;
  let maxX = -Infinity;
  for (let py = 0; py < _canvas.height; py++) {
    const row = py * _canvas.width;
    for (let px = 0; px < _canvas.width; px++) {
      if (data[(row + px) * 4 + 3] > 16) {
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
      }
    }
  }

  let nudge = 0;
  if (maxX >= minX) {
    const inkCenterCss = ((minX + maxX) / 2) / dpr;
    const advanceCenterCss = originX + advance / 2;
    nudge = advanceCenterCss - inkCenterCss; // > 0 → nach rechts schieben
  }
  _nudgeCache.set(key, nudge);
  return nudge;
}

/**
 * Notification-Badge im iOS-Stil: eine Ziffer ergibt einen Kreis, mehr Ziffern
 * lassen ihn zur Kapsel wachsen.
 *
 * Font: bewusst die fest geladene App-Font (IBM Plex Sans 600) statt `system-ui`
 * (das je OS eine andere Font mit anderen Metriken ist) — so ist die *vertikale*
 * Zentrierung auf PC und iPhone identisch. Gewicht 600, weil die App 700 nicht
 * lädt (würde faux-bold mit inkonsistenten Metriken erzeugen).
 * Horizontal wird per Pixel-Analyse pro Wert optisch nachzentriert (siehe oben).
 *
 * Positionierung (absolute top/right) kommt aus dem jeweiligen Kontext-CSS.
 */
function NavBadge({ count, size = 14, fontSize = 9, pad = 4 }) {
  const text = count > 99 ? '99+' : String(count);

  const [fontsReady, setFontsReady] = useState(
    () => typeof document === 'undefined' || !document.fonts
      || document.fonts.check(`${FONT_WEIGHT} ${fontSize}px 'IBM Plex Sans'`)
  );

  useEffect(() => {
    if (fontsReady || typeof document === 'undefined' || !document.fonts) return undefined;
    let alive = true;
    document.fonts.ready.then(() => { if (alive) setFontsReady(true); });
    return () => { alive = false; };
  }, [fontsReady]);

  const nudgeX = useMemo(
    () => (fontsReady ? inkCenterNudge(text, fontSize) : 0),
    [text, fontSize, fontsReady]
  );

  return (
    <span
      className="navBadge"
      style={{ '--nav-badge-size': `${size}px`, '--nav-badge-pad': `${pad}px` }}
    >
      <span
        className="navBadgeNum"
        style={{ '--nav-badge-font': `${fontSize}px`, transform: `translateX(${nudgeX.toFixed(2)}px)` }}
      >
        {text}
      </span>
    </span>
  );
}

export default NavBadge;
